import { Pathology } from '@app/core/models/pathology';
import { Injectable } from '@angular/core';
import { PATHOLOGIES } from '@app/shared/data/pathologies';
import { AppState } from '@app/store';
import {
  selectConsultationPatient,
  selectLastConsultation,
  selectConsultationRenalDetectRecommendation,
} from '@app/store/consultation/consultation.selectors';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { setSmartRecoPathologies, setSmartRecoFormValues } from '../actions/smart-reco-patient.actions';
import { getPetFromFormValues, getProfileFormValues, getWeightFormValues } from '@app/shared/utils/static-helpers/pet-formatter-helper';
import { PetService, VetService } from '@app/core/services';
import {
  autoSaveNewPet,
  autoSaveNewPetError,
  autoSaveNewPetSuccess,
  setPatientProfile,
  setPatientWeight,
} from '@app/store/patient-profile';
import { Tool } from '@app/shared/utils';
import { selectOngoingPet, selectProfile } from '@app/store/patient-profile/patient-profile.selectors';
import { selectPathologies } from '../selectors/smart-reco-patient.selectors';
import { selectCurrentClinicCountry } from '@app/store/vet';
import { isNotNullOrUndefined } from '@app/shared';
import { Pet } from '@app/core/models';
import { buildPetDraftFromPetFormValues } from '@app/shared/utils/static-helpers/pet-draft-helper';
import { of } from 'rxjs';

@Injectable()
export class SmartRecoPatientEffects {
  /**
   * When the patient is set, we update profile and weight form values
   */
  setSmartRecoFormValues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSmartRecoFormValues),
      withLatestFrom(
        this.store$.select(selectConsultationPatient),
        this.store$.select(selectConsultationRenalDetectRecommendation),
        this.store$.select(selectLastConsultation),
        this.vetService.currentBigMeasurementUnit$,
      ),
      filter(([_, patient, renalDetectRecommendation]) => !!patient || !!renalDetectRecommendation),
      switchMap(([_, patient, renalDetectRecommendation, lastConsultation, currentBigMeasurementUnit]) => [
        setSmartRecoPathologies({
          value: this.getPathologies(renalDetectRecommendation?.pathologies),
        }),
        setPatientProfile({ profile: getProfileFormValues(patient?.pet) }),
        setPatientWeight({
          weight: getWeightFormValues(patient?.pet, lastConsultation?.visit, currentBigMeasurementUnit),
          tool: Tool.SmartReco,
        }),
      ]),
    ),
  );

  /**
   * When the weight form is complete, we update pathologies if needed;
   * Remove previously declared BCS related pathologies;
   * Add new BCS related pathology if needed
   */
  setWeightPathologies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setPatientWeight),
      filter((a) => a.tool === Tool.SmartReco),
      withLatestFrom(this.store$.select(selectPathologies)),
      filter(([{ weight }]) => !!weight && !!weight.bcs),
      map(([{ weight }, pathologies]) => {
        const nonBcsRelatedPathologies = pathologies.filter((p) => !p.bcs);
        const weightPathology = PATHOLOGIES.find((p) => p.bcs === weight.bcs);
        return weightPathology ? [...nonBcsRelatedPathologies, weightPathology] : nonBcsRelatedPathologies;
      }),
      switchMap((pathologies) => [
        setSmartRecoPathologies({
          value: pathologies,
        }),
      ]),
    ),
  );

  autoSaveNewPet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(autoSaveNewPet),
      filter((a) => a.tool === Tool.SmartReco),
      withLatestFrom(
        this.store$.select(selectOngoingPet),
        this.store$.select(selectProfile),
        this.store$.select(selectPathologies),
        this.vetService.vetId$,
        this.store$.select(selectCurrentClinicCountry),
      ),
      switchMap(([, ongoingPet, profile, pathologies, vetId, country]) => {
        if (isNotNullOrUndefined(ongoingPet)) {
          const pet: Pet = getPetFromFormValues(profile);
          return this.vetService.updatePet(ongoingPet.id, pet).pipe(
            map((pet) => autoSaveNewPetSuccess({ pet })),
            catchError(() => of(autoSaveNewPetError())),
          );
        } else {
          return this.petService.createPetDraft(buildPetDraftFromPetFormValues(profile, vetId, country, pathologies)).pipe(
            map((pet) => autoSaveNewPetSuccess({ pet })),
            catchError(() => of(autoSaveNewPetError())),
          );
        }
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private petService: PetService,
    private vetService: VetService,
  ) {}

  /**
   * Map pet data to weight form values
   */
  private getPathologies(pathologies: string[] = []): Pathology[] {
    return PATHOLOGIES.filter((p) => pathologies.includes(p.value));
  }
}
